import { memo, useEffect, useRef, useState } from 'react';

import '@organisms/ORom/style.css';
import { useTypedDispatch, useTypedSelector } from '@stores/index';
import {
	ETransitions,
	TTransitions,
	clearPoseData,
	completedExercise,
	editPatientResults,
	exerciseValue,
	getBodyPointsVisible,
	goToExercise,
	nextTransition,
	savePatientResults,
	setCameraId,
	updateUser,
} from '@stores/rom/main';
import { Row } from 'antd';
import RomData from './RomData';
import { NormalizedLandmark } from '@mediapipe/tasks-vision';
import { useLocation } from 'react-router-dom';
import { ControlsProvider } from '@pages/PostureScan/context/Controls.context';

interface SaveResultsPayload {
	userId: string;
	romSessionId: string;
	title: string;
	screenshot: string;
	coordinates: string;
	result?: {
		[key: string]: string | number | object[] | boolean | undefined;
	};
	results?: {
		create?: object[];
	};
}

interface ORomProps {
	isDashboard?: boolean;
}

const ORom = (props: ORomProps) => {
	const { isDashboard } = props;
	const { state } = useLocation();
	const { resultsExercises } = useTypedSelector(state => state.rom.main);
	const [isFullscreen, setFullscreen] = useState(false);
	const [flipCamera, setFlipCamera] = useState(false);
	const [isMenuOpened, setMenuOpen] = useState(false);
	const [isTutorialOpened, setTutorialOpen] = useState(false);
	const [isSwitchMode, setSwitchMode] = useState(false);
	const [isSplashOpened, setSplashOpen] = useState(true);
	const [isVideoPause, setVideoPause] = useState(false);
	const [isValidResult, setValidResult] = useState<boolean | null>(null);
	const [isCompleted, setCompleted] = useState(false);
	const [isManual, setIsManual] = useState(false);
	const [isInfoOpened, setInfoOpen] = useState(false);
	const fullscreenRef = useRef<HTMLDivElement>(null);
	const [completionLoader, setCompletionLoader] = useState(false);

	const dispatch = useTypedDispatch();
	const {
		session,
		isCustom,
		pose,
		transition,
		metricsData,
		isRepetingExercise,
		enableToSendResult,
		exercises,
		currentExercise,
		cameraId,
	} = useTypedSelector(state => state.rom.main);
	const { selectedRom } = useTypedSelector(state => state.rom.customRom);

	const user = useTypedSelector(state => {
		const { selectedUser } = state.contacts.main;
		const user = state.user;
		return user.isPhysioterapist ? selectedUser : user;
	});
	const wakeLockRef = useRef<WakeLockSentinel | null>(null);

	const onToggleMenu = () => {
		setTutorialOpen(false);
		setMenuOpen(!isMenuOpened);
		setInfoOpen(false);
	};

	const onToggleInfo = () => {
		setInfoOpen(!isInfoOpened);
		setMenuOpen(false);
	};

	const onToggleTutorial = () => {
		setMenuOpen(false);
		setTutorialOpen(!isTutorialOpened);
	};

	const onTogglesSwitchMode = () => {
		setSwitchMode(!isSwitchMode);
	};

	const onTogglesSplashPage = () => {
		setSplashOpen(!isSplashOpened);
	};

	const onExerciseValueAndCoordinates = (
		value: number,
		coordinates: NormalizedLandmark[],
	) => {
		dispatch(exerciseValue({ value, coordinates }));
	};

	const onBodyPointsVisible = (value: boolean) => {
		dispatch(getBodyPointsVisible(value));
	};

	const switchCamera = async () => {
		try {
			// Request camera access with specific constraints
			await navigator.mediaDevices.getUserMedia({
				audio: true,
				video: { width: 1280, height: 720, frameRate: 30 },
			});

			// Get the list of video input devices
			const devices = await navigator.mediaDevices.enumerateDevices();
			const cameras = devices.filter(device => device.kind === 'videoinput');

			if (cameras.length > 0) {
				// Find the current camera index
				const currentCameraIndex = cameras.findIndex(
					camera => camera.deviceId === cameraId,
				);
				const nextCameraIndex = (currentCameraIndex + 1) % cameras.length;
				const nextCamera = cameras[nextCameraIndex];

				if (nextCamera?.deviceId) {
					dispatch(setCameraId(nextCamera.deviceId));
				} else {
					// Retry switching if deviceId is empty
					setTimeout(switchCamera, 1000);
				}
			}
		} catch (error) {
			console.error('Error switching camera:', error);
		}
	};

	const onTogglePauseVideo = () => {
		setVideoPause(!isVideoPause);
	};

	const onFullscreen = async () => {
		if (isFullscreen) {
			await document.exitFullscreen();
		} else if (fullscreenRef.current) {
			await fullscreenRef.current.requestFullscreen();
		}
	};

	const onNextTransition = (transition: TTransitions) =>
		dispatch(nextTransition(transition));

	const exerciseNumber = exercises?.findIndex(
		exer => exer.id === currentExercise?.id,
	);

	useEffect(() => {
		if (resultsExercises.length !== exercises?.length) {
			const resultIds = resultsExercises.map(result => String(result.id));
			const missingExercise = exercises?.find(
				exercise => !resultIds.includes(String(exercise.id)),
			);

			if (missingExercise) {
				dispatch(goToExercise(missingExercise));
			}
		} else {
			setCompletionLoader(true);
			setCompleted(true);
		}
	}, [resultsExercises, exercises]);

	const onSavePhysicalAssessmentsMetrics = async () => {
		const payload: SaveResultsPayload = {
			userId: user?.id,
			romSessionId: state?.isEdit ? state?.sessionId : session?.id ?? '',
			title: currentExercise?.name
				? currentExercise?.name
				: currentExercise?.title ?? '',
			screenshot: metricsData?.screenshot,
			coordinates: metricsData?.coordinates,
		};

		if (currentExercise?.exercises?.length) {
			const asyncTasks = currentExercise.exercises.map(
				async (exercise, index) => {
					const value = isCustom
						? pose?.angleResults[index]
						: pose?.angleResult;
					const romPatientResults = selectedRom?.romPatientResults?.find(res =>
						res?.results?.some(
							resultItem => resultItem.romProgramExercise?.id === exercise.id,
						),
					);
					const result = romPatientResults?.results?.find(
						resultItem => resultItem.romProgramExercise?.id === exercise.id,
					);
					if (state?.isEdit && result?.romPatientResultId != undefined) {
						payload.result = {};
						payload.result = {
							value: value,
							normal: exercise?.normal,
							min: exercise?.min,
							max: exercise?.max || exercise?.normal,
							wfl: exercise?.wfl,
							outOfRange:
								typeof exercise?.min === 'number' &&
								typeof exercise?.max === 'number'
									? value < exercise.min && value > exercise.max
									: false,
						};

							await dispatch(
								editPatientResults({
									editData: payload,
									id: result.romPatientResultId,
								}),
							);
							dispatch(completedExercise(currentExercise));
					} else {
						payload.results = { create: [] };
						payload.results?.create?.push({
							value: value,
							normal: exercise?.normal,
							min: exercise?.min,
							max: exercise?.max || exercise?.normal,
							wfl: exercise?.wfl,
							outOfRange:
								typeof exercise?.min === 'number' &&
								typeof exercise?.max === 'number'
									? value < exercise.min && value > exercise.max
									: false,
							romProgramExerciseId: exercise?.id,
						});
						await dispatch(savePatientResults(payload));
						dispatch(completedExercise(currentExercise));
					}
				},
			);
			await Promise.all(asyncTasks);
		}

		dispatch(clearPoseData());
	};

	useEffect(() => {
		const onFullscreenChange = () =>
			setFullscreen(Boolean(document.fullscreenElement));

		document.addEventListener('fullscreenchange', onFullscreenChange);
		return () =>
			document.removeEventListener('fullscreenchange', onFullscreenChange);
	}, []);

	useEffect(() => {
		const enumerateCameras = async () => {
			try {
				if (navigator.mediaDevices?.enumerateDevices) {
					const devices = await navigator.mediaDevices.enumerateDevices();
					const cameras = devices.filter(
						device => device.kind === 'videoinput',
					);
					setFlipCamera(cameras.length > 1);
				}
			} catch (error) {
				console.error('Error enumerating devices:', error);
			}
		};

		enumerateCameras().then(() => {
			switchCamera(); // Ensure this runs after camera detection
		});

		return () => setFlipCamera(false);
	}, []);

	useEffect(() => {
		return () => {
			setFullscreen(false);
			setFlipCamera(false);
			setMenuOpen(false);
			setTutorialOpen(false);
			setSwitchMode(false);
			setSplashOpen(true);
			setVideoPause(false);
			setValidResult(null);
			setCompleted(false);
			setIsManual(false);
			setInfoOpen(false);
		};
	}, []);

	const requestWakeLock = async () => {
		try {
			if ('wakeLock' in navigator && wakeLockRef.current === null) {
				wakeLockRef.current = await navigator.wakeLock.request('screen');
				wakeLockRef?.current?.addEventListener('release', () => {
					console.log('Wake Lock was released');
				});
				console.log('Wake Lock is active');
			}
		} catch (err) {
			console.error('Wake Lock request failed:', err);
		}
	};

	const releaseWakeLock = async () => {
		try {
			if (wakeLockRef.current) {
				await wakeLockRef.current.release();
				console.log('Wake Lock was released');
				wakeLockRef.current = null;
			} else {
				console.log('No Wake Lock to release');
			}
		} catch (err) {
			console.error('Wake Lock release failed:', err);
		}
	};

	useEffect(() => {
		requestWakeLock();

		return () => {
			releaseWakeLock();
		};
	}, []);

	useEffect(() => {
		dispatch(updateUser(user));
	}, [user, dispatch]);

	useEffect(() => {
		if (transition?.value === ETransitions.INTRO) {
			setVideoPause(false);
		}
	}, [transition, isRepetingExercise]);

	useEffect(() => {
		if (transition?.value === ETransitions.RESULT) {
			setValidResult(enableToSendResult);
		} else {
			setValidResult(null);
		}

		return () => setValidResult(null);
	}, [transition, enableToSendResult]);

	return (
		<Row align={'top'} className="select-none" justify="center">
			<ControlsProvider>
				<RomData
					isSplashOpened={
						!state?.isDashboard && !isDashboard && !isCustom && isSplashOpened
					}
					completionLoader={completionLoader}
					setCompletionLoader={setCompletionLoader}
					onToggleMenu={onToggleMenu}
					isDashboard={isDashboard ?? false}
					isValidResult={isValidResult}
					onSavePhysicalAssessmentsMetrics={onSavePhysicalAssessmentsMetrics}
					isVideoPause={isVideoPause}
					onTogglePauseVideo={onTogglePauseVideo}
					isFullscreen={isFullscreen}
					onToggleTutorial={onToggleTutorial}
					onFullscreen={onFullscreen}
					onTogglesSwitchMode={onTogglesSwitchMode}
					flipCamera={flipCamera}
					switchCamera={switchCamera}
					isSwitchMode={isSwitchMode}
					isMenuOpened={isMenuOpened}
					isTutorialOpened={isTutorialOpened}
					onTogglesSplashPage={onTogglesSplashPage}
					onExerciseValueAndCoordinates={onExerciseValueAndCoordinates}
					onBodyPointsVisible={onBodyPointsVisible}
					onNextTransition={onNextTransition}
					fullscreenRef={fullscreenRef}
					isCompleted={isCompleted}
					setCompleted={setCompleted}
					setIsManual={setIsManual}
					isManual={isManual}
					onToggleInfo={onToggleInfo}
					isInfoOpened={isInfoOpened}
				/>
			</ControlsProvider>
		</Row>
	);
};

export default memo(ORom);
